import { Injectable, OnDestroy } from '@angular/core';
import { ToastService } from '../../services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { RequestApiClient } from '../../services/api-clients/request.apiclient';
import { BehaviorSubject, lastValueFrom, Subscription } from 'rxjs';
import { ModalOpenerService } from '../../modals/modal-opener.service';
import CloudItem from '../../models/clouditem';
import { ExternalCloudLocationsApiClient } from '../../services/api-clients/external-cloudlocations.api-client';
import { LoggingService } from '../../services/logging.service';
import { extractFilename } from '../../services/content-disposition';
import { RequestStatuses } from '../../constants/status.enum';
import { DossierService } from '../../services/dossier.service';
import { User } from 'src/app/models/user';
import { EventLogApiClient, EventLogModel } from 'src/app/services/api-clients/event-log.api-client';
import { StringHelper } from 'src/app/directives/string-helper';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { PlatformHubService } from 'src/app/services/platformhub.service';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { OrganisationApiClient } from 'src/app/services/organisation.apiclient';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { UpdateExistingActorModel, UpdateExistingRequestActorModel } from 'src/app/models/api/update-existing-actor.model';
import { RequestStatusPersonDialogModal } from './requeststatuspersondialog/request-status-person-dialog.modal';
import { ActorSendGroupStatusInformationModel, ActorStatusInformationModel } from 'src/app/models/api/actor-status-information.model';
import { ActorApiClient } from 'src/app/services/api-clients/actor.api-client';
import { DocumentTypeEnum, DocumentTypesForViewingAsHtml, XbrlDocumentTypes } from 'src/app/constants/document-type.enum';
import { DocumentModel } from 'src/app/models/document.model';
import { PdfPreparationDialogForStatus } from '../general/pdf-preparation-dialog/pdf-preparation-dialog.component';
import { DocumentApiClient } from 'src/app/services/document.apiclient';
import { ClipboardService } from 'src/app/services/clipboard.service';
import { TheNaviGator } from 'src/app/services/the-navi-gator.service';
import { StatusSendgroupApiClient } from 'src/app/services/api-clients/status-sendgroup.api-client';
import { EmailPreviewDialogComponent, EmailPreviewModalOptions } from '../requestbuilder/email-preview-dialog/email-preview-dialog.component';
import { PersonApiClient } from 'src/app/services/api-clients/person.api-client';
import { saveAs } from 'file-saver';
import { DeleteRequestModal } from 'src/app/modals/delete-request/delete-request.modal';
import { EventLogTypeEnum } from 'src/app/constants/event-log-type.enum';
import { StatusRequestModel } from 'src/app/models/status/status-request.model';
import { StatusUpdateSendGroupModel } from 'src/app/models/status/status-sendgroup.model';
import { StatusDocumentModel } from 'src/app/models/status/status-document.model';
import { StatusRequestApiClient } from 'src/app/services/api-clients/status-request.api-client';
import { DocumentPagesService } from 'src/app/services/document-pages.service';
import { Provider } from 'src/app/models/signatureProvider';
import { EngineService } from 'src/app/services/engine.service';
import { formatDate } from '@angular/common';
import { NoteModel } from 'src/app/models/notes/note.model';
import { NoteApiClient } from 'src/app/services/api-clients/note.api-client';
import { BuilderRequestAutoExportSettingsModel } from 'src/app/models/builder/builder-request-auto-export-settings.model';
import { EditSignatureDataDialogComponent, EditSignatureDataModalOptions } from './edit-signature-data-dialog.modal/edit-signature-data-dialog.modal';
import { Workflow } from 'src/app/models/workflow';
import { WorkflowService } from 'src/app/services/workflow.service';
import { SignatureProviderTypeEnum } from 'src/app/constants/signature-provider-type.enum';
import { SetNormalizedEmail } from 'src/app/directives/set-normalized-email.function';
import { XbrlFilingData } from 'src/app/models/xbrlFilingData.model';
import { FilingDataApiClient } from 'src/app/services/api-clients/filing-data.api-client';
import { ExternalContentProviders } from 'src/app/constants/ExternalContentProviders.enum';

@Injectable({
  providedIn: 'root'
})
export class RequestStatusService implements OnDestroy {
  private _allSignatureProviders!: Provider[];
  private _allowedSignatureProviders!: Provider[];

  translations = <any>{};
  request: StatusRequestModel = {} as StatusRequestModel;
  sendgroups: ActorSendGroupStatusInformationModel[] = [];
  actor: ActorStatusInformationModel = new ActorStatusInformationModel;
  dossierPersonId!: string;
  hasEditRights: boolean = false;
  user!: User;
  usersubscription: Subscription;
  requestSignalRSubscription: Subscription;
  actorSubscription: Subscription | null = null;

  eventlog!: EventLogModel[];
  eventLogSubscription: Subscription | null = null;
  notes!: NoteModel[];
  updateNotesSubscription: Subscription | null = null;
  newNotesSubscription: Subscription | null = null;
  removedNotesSubscription: Subscription | null = null;

  stringHelper = new StringHelper();
  allowedToChangeOwnership: boolean = false;
  private _signatureProviders: Provider[] = [];

  workflowData?: Workflow;
  public allowedSignatureProvidersByWorkflow: string[] = [];
  placeholdersStep3: boolean = false;
  public allowedSignatureProvidersByPlaceholder: any[] = [];

  /**
   * Whether when picking SignatureProviders ESeal is allowed
   * -Default false, only allowed for ondertekenverzoek/Pdf (21-07-2025)
   * -READS from workflow
   */
  public get allowESealSignatureProvider(): boolean {
    return this.workflowData?.step3?.allowsESeal!;
  };

  /**
   * Opened/Collapsed sendgroups, so it can be retained on refresh
   */
  collapsedSendgroups: string[] = [];

  public actorId$: BehaviorSubject<string> = new BehaviorSubject("");
  public sendgroups$: BehaviorSubject<ActorSendGroupStatusInformationModel[]> = new BehaviorSubject({} as ActorSendGroupStatusInformationModel[]);
  public selectedRequestId$: BehaviorSubject<string> = new BehaviorSubject("");
  public selectedRequest$: BehaviorSubject<StatusRequestModel> = new BehaviorSubject({} as StatusRequestModel);
  public eventLog$: BehaviorSubject<EventLogModel[]> = new BehaviorSubject([] as EventLogModel[]);
  public notes$: BehaviorSubject<NoteModel[]> = new BehaviorSubject([] as NoteModel[]);
  public signatureProviders$: BehaviorSubject<Provider[]> = new BehaviorSubject([] as Provider[]);

  requestId!: string;
  actorId!: string;
  isMobile: boolean = false;

  constructor(
    private clipboardService: ClipboardService,
    private cloudLocationsApiClient: ExternalCloudLocationsApiClient,
    private dossierService: DossierService,
    private loggingService: LoggingService,
    private modalOpenerService: ModalOpenerService,
    private requestService: RequestApiClient,
    private toastService: ToastService,
    private translateService: TranslateService,
    private authenticationService: AuthenticationService,
    private platformHub: PlatformHubService,
    private eventLogApiClient: EventLogApiClient,
    private organisationApiClient: OrganisationApiClient,
    private modalService: NgbModal,
    private actorApiClient: ActorApiClient,
    private personApiClient: PersonApiClient,
    private documentService: DocumentApiClient,
    private platformHubService: PlatformHubService,
    private sendgroupService: StatusSendgroupApiClient,
    private theNaviGator: TheNaviGator,
    private statusRequestApiClient: StatusRequestApiClient,
    private documentPageService: DocumentPagesService,
    private engineService: EngineService,
    private noteApiClient: NoteApiClient,
    private workflowService: WorkflowService,
    private navigate: TheNaviGator,
    private filingDataApiClient: FilingDataApiClient) {
    this.usersubscription = this.authenticationService.currentUser.subscribe((data) => {
      this.user = data;
    });
    this.requestSignalRSubscription = this.platformHub.statusRequest$.subscribe((request: StatusRequestModel) => {
      if (request && request.id === this.requestId) {
        this.request = request;
        this.selectedRequest$.next(this.request);
        this.getSignatureProviders();
        if (this.request.workflowId) {
          this.initializeForWorkflow(this.request.workflowId);
        }
        if (request.workgroupGuid && !request.workgroupName) {
          request.workgroupName = this.authenticationService.getWorkGroupName(request.workgroupGuid);
        }
        this.hasEditRights = this.request?.ownerGuid === this.authenticationService.currentUserValue.sub || this.authenticationService.MyWorkgroups.some(w => w.guid === this.request.workgroupGuid);
        this.allowedToChangeOwnership = !this.hasEditRights && (this.user.isOrganisationAdmin || this.user.isWhitelabelAdmin || this.user.isSystemAdmin);
        request.documentInfo.filter(s => s.documentType == DocumentTypeEnum.RegularPdf).forEach(document => {
          this.documentPageService.resetAlreadyLoadedPagesAndReLoadThem(request.id, document.id);
        });
      }
    });


    this.isMobile = /(iPhone|iPod|iPad|Android|webOS|BlackBerry|IEMobile|Opera Mini)/.exec(navigator.userAgent) !== null;
    this.getTranslations();
    translateService.onLangChange.subscribe(() => {
      this.getTranslations();
    });
  }
  ngOnDestroy() {
    if (this.usersubscription) this.usersubscription.unsubscribe();
    if (this.actorSubscription) this.actorSubscription.unsubscribe();
    if (this.eventLogSubscription) this.eventLogSubscription.unsubscribe();
    if (this.updateNotesSubscription) this.updateNotesSubscription.unsubscribe();
    if (this.newNotesSubscription) this.newNotesSubscription.unsubscribe();
    if (this.requestSignalRSubscription) this.requestSignalRSubscription.unsubscribe();
    if (this.requestId) {
      this.platformHubService.StopListeningToEventLog(this.requestId);
      this.platformHubService.StopListeningToActorList(this.requestId);
    }
  }

  public hasUserEditRights() {
    if (Object.keys(this.request).length === 0) {
      return false;
    }
    return (this.request?.ownerGuid === this.authenticationService.currentUserValue.sub || this.authenticationService.MyWorkgroups.some(w => w.guid === this.request.workgroupGuid));
  }

  public isUserAllowedToChangeOwnership() {
    return this.hasUserEditRights() && this.request?.ownerGuid !== this.authenticationService.currentUserValue.sub;
  }

  public openDashboard() {
    this.theNaviGator.To_Dashboard();
  }

  /** Set the selected requestId to the given Info */
  public setSelectedRequestId(requestId: string) {
    // verify current with incoming:
    if (this.requestId == requestId) {
      // no change in Id -> no action
      return;
    }
    let previousRequestGuid = this.requestId;
    if (requestId === previousRequestGuid) return;
    this.requestId = requestId;

    if (requestId && requestId !== "") {
      this.getRequest();
      this.selectedRequestId$.next(requestId);
    }

    if (!this.requestId) {
      // we are resetting requestGuid;
      if (previousRequestGuid) {
        this.platformHubService.StopListeningToEventLog(previousRequestGuid);
        this.platformHubService.StopListeningToActorList(previousRequestGuid);
      }
      this.request = {} as StatusRequestModel;
      this.sendgroups = [];
      this.eventlog = [];
      this.notes = [];
      this.selectedRequest$.next(this.request);
      this.sendgroups$.next(this.sendgroups);
      this.notes$.next(this.notes);
      return;
    }
    if (previousRequestGuid == this.requestId) {
      // no changes
      return;
    }

    // we should stop listening to previous
    if (previousRequestGuid) {
      this.platformHubService.StopListeningToEventLog(previousRequestGuid);
      this.platformHubService.StopListeningToActorList(previousRequestGuid);
      this.platformHubService.StopListeningToNotes(previousRequestGuid);
    }

    // now we are constantly listening on changing request, but we aren't unsubscribing.
    // we unsubscribe when the component is destroyed, this means it is to far apart.
    // listen/stop listening should both be orchestrated from the same place
    // prevent multi subscription from being started, stop the previous subscription manually:
    if (this.actorSubscription) {
      this.actorSubscription.unsubscribe();
    }
    this.actorSubscription = this.platformHub.ListenToActorList(this.requestId).subscribe(newActorList => {
      this.sendgroups = newActorList;
      this.sendgroups$.next(this.sendgroups);
    });
    // prevent multi subscription from being started, stop the previous subscription manually:
    if (this.eventLogSubscription) {
      this.eventLogSubscription.unsubscribe();
    }
    this.eventLogSubscription = this.platformHubService.ListenToEventLog(this.requestId).subscribe(newEventLog => {
      if (!this.eventlog) {
        this.eventlog = [];
      }
      this.eventlog.push(newEventLog);
      this.eventLog$.next(this.eventlog);
    });

    // prevent multi subscription from being started, stop the previous subscription manually:
    if (this.newNotesSubscription) {
      this.newNotesSubscription.unsubscribe();
    }
    if (this.updateNotesSubscription) {
      this.updateNotesSubscription.unsubscribe();
    }
    if (this.removedNotesSubscription) {
      this.removedNotesSubscription.unsubscribe();
    }

    // doubles so we listen than subscribe BOTH:
    this.platformHubService.ListenToNotes(this.requestId);
    this.newNotesSubscription = this.platformHubService.newNote$.subscribe(newNote => {
      if (!this.notes) {
        this.notes = [];
      }
      this.notes.push(newNote);
      this.notes$.next(this.notes);
    });
    this.updateNotesSubscription = this.platformHubService.updateNotes$.subscribe(changedNote => {
      if (!this.notes) {
        this.notes = [];
      }
      this.notes = this.notes.map(note =>
        note.noteGuid === changedNote.noteGuid ? changedNote : note
      );
      this.notes$.next(this.notes);
    });
    this.removedNotesSubscription = this.platformHubService.deletedNote$.subscribe(noteGuid => {
      if (!this.notes) {
        this.notes = [];
      }
      this.notes = this.notes.filter(note => note.noteGuid !== noteGuid);
      this.notes$.next(this.notes);
    });
  }

  setSendgroups(sendgroups: ActorSendGroupStatusInformationModel[]) {
    this.sendgroups = sendgroups;
    this.sendgroups$.next(this.sendgroups);
  }

  public downloadRequest(requestId: string) {
    this.toastService.showDefault(this.translations.DownloadTitle, this.translations.DownloadBody);

    this.requestService.downloadRequest(requestId)
      .then(value => {
        lastValueFrom(value)
          .then((data: any) => {
            let filename = extractFilename(data.headers.get("Content-Disposition")!);
            this.saveFile(data.body, filename);
          })
          .catch(() => this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestsDownloadError + ""));
      })
      .catch(() => this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestsDownloadError + ""));
  }

  public exportRequest(requestId: string, requestName: string) {
    const modalRef = this.modalOpenerService.CloudDocumentExport(requestId, requestName, true);
    modalRef.componentInstance.close.subscribe(async (event: CloudItem) => {
      modalRef.close();
    });
  }

  public downloadDocument(requestGuid: string, document: StatusDocumentModel) {
    this.toastService.showDefault(this.translations.DownloadTitle, this.translations.DownloadBody);

    this.documentService.downloadDocument(requestGuid, document.id).subscribe({
      next: (data: any) => {
        let filename = extractFilename(data.headers.get("Content-Disposition")!);
        this.saveFile(data.body, filename);
      }, error: () => {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsDocumentDownloadError + "")
      }
    });
  }

  public exportDocument(requestGuid: string, document: StatusDocumentModel) {
    const modalRef = this.modalOpenerService.CloudDocumentExport("", document.name, false);
    modalRef.componentInstance.close.subscribe(async (event: CloudItem) => {
      if (event && event.folderId !== null && event.folderId !== undefined && event.parentId !== null && event.parentId !== undefined) {
        try {
          this.cloudLocationsApiClient.saveDocumentToParentExternalContent(event.provider, event.parentId, event.folderId, requestGuid, document.id).subscribe({
            next: () => {
              this.toastService.showDefault(this.translations.ExportTitle, this.translations.ExportBody);
            },
            error: () => {
              this.toastService.showError(this.translations.error, this.translations.toastsDocumentExportError);
            }
          })
        } catch (error: any) {
          this.toastService.showError(this.translations.error, this.translations.toastsDocumentExportError);
        }
        modalRef.close();
      } else {
        modalRef.close();
      }
    });
  }

  public purgeRequest(requestId: string) {
    this.requestService.purgeRequest(requestId)
      .then(value => {
        lastValueFrom(value)
          .then(() => this.toastService.showSuccess(this.translations.toastsPurged, this.translations.toastsRequestPurged))
          .catch(() => this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestsPurgeError + ""));
      })
      .catch(() => this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestsPurgeError + ""));
  }


  public openWithdrawModal(request: StatusRequestModel) {
    let withdrawAllowedStatussen: string[] = RequestStatuses.ThatCanBeWithdrawn;

    if (request === undefined || !request) {
      return;
    }
    if (!withdrawAllowedStatussen.includes(request.currentStatus)) {
      this.toastService.showWarning(this.translations.error, this.translations.notAllowedWithdrawError);
      return;
    }

    this.modalOpenerService.WithDrawnRequest([request.id]);
  }

  public openDeleteModal(request: StatusRequestModel) {
    let deleteAllowedStatussen: string[] = RequestStatuses.ThatCanBeDeleted;

    if (request === undefined || !request) {
      return;
    }
    if (!deleteAllowedStatussen.includes(request.currentStatus)) {
      this.toastService.showWarning(this.translations.error, this.translations.notAllowedDeleteError);
      return;
    }

    const deleteRequestModalRef = this.modalOpenerService.DeleteRequest([request.id]);
    (deleteRequestModalRef.componentInstance as DeleteRequestModal).deleteItems.subscribe(() => {
      this.theNaviGator.To_Dashboard();
    });
  }

  public openStatusViewLogModal(request: StatusRequestModel) {
    if (request === undefined || !request) {
      return;
    }
    this.modalOpenerService.StatusViewLog();
  }

  public openEditReqeustModal(request: StatusRequestModel) {
    if (request === undefined || !request) {
      return;
    }
    this.modalOpenerService.EditRequest(request);
  }



  public async editFilingData() {
    let filingData: XbrlFilingData = new XbrlFilingData();
    (await this.filingDataApiClient.get(this.requestId)).subscribe({
      next: (data: XbrlFilingData) => {
        filingData = data;
        this.modalOpenerService.EditFilingData(this.requestId, filingData);
      },
      error: (error: any) => {
        console.error("Error fetching filing data:", error);
      }
    });
  }

  public goToRequest(request: StatusRequestModel) {
    this.navigate.To_Viewer_In_A_New_Window(request.id);
  }


  public restoreRequest(request: StatusRequestModel) {
    this.requestService.restoreRequest(request.id, request.requestName).subscribe(() => {
      this.toastService.showSuccess(this.translations.toastsRestored, this.translations.toastsRequestRestored);
    });
  }

  public async becomeOwner(requestId: string) {
    let changeOwner = await this.dossierService.ChangeOwner(requestId);
    await lastValueFrom(changeOwner);

    this.toastService.showSuccess(this.translations.toastsChanged, this.translations.toastsRequestOwnerChanged);
  }

  public editPerson(actor: ActorStatusInformationModel) {
    if (actor === null) return;
    let ngbModalOptions: NgbModalOptions = {
      centered: true,
      size: 'md',
      backdrop: 'static'
    };
    const modalRef = this.modalService.open(RequestStatusPersonDialogModal, ngbModalOptions);
    modalRef.componentInstance.personToEdit = actor.editPersonModel;
    modalRef.componentInstance.actorGuid = actor.personGuid;
    modalRef.componentInstance.requestGuid = this.requestId;
    modalRef.closed.subscribe(
      (actorInformation: UpdateExistingActorModel) => {
        if (!actorInformation) {
          return;
        }
        let inviteId = actor.inviteId;
        let newEmail = SetNormalizedEmail(actorInformation.email);
        let newMobile = actorInformation.mobile;
        let newLanguage = actorInformation.language;
        let newPersonalMessage = actorInformation.personalMessage;
        let personGuids = actorInformation.personGuids;
        this.actorApiClient.UpdateActorPerson(this.requestId, inviteId, newEmail, newMobile, newLanguage, newPersonalMessage, personGuids).subscribe((value) => {
          if (this.actor.isNextActor) {
            this.toastService.showSuccess(this.translations.success, this.translations.toastPPersonChangedWithInvite);
          } else {
            this.toastService.showSuccess(this.translations.success, this.translations.toastPersonChangedNoInvite);
          }
        },
          errorResponse => {
            if (errorResponse instanceof HttpErrorResponse) {
              // specific error for when you are trying to assign an existing user with a different name
              if (errorResponse.status == HttpStatusCode.Conflict) {
                this.toastService.showErrorByKey("Toasts.CannotAssignADifferentUser");
              }
              // specific error for when trying to assign a person already on request
              else if (errorResponse.status == HttpStatusCode.NotAcceptable) {
                // same message as below but we can make a difference if needed
                this.toastService.showErrorByKey("Toasts.ErrorDuringUserEditAlreadyAssignedToRequest");
              } else if (errorResponse.status == HttpStatusCode.BadRequest) {
                // same message as below but we can make a difference if needed
                this.toastService.showErrorByKey("Toasts.ErrorDuringUserEditRequestStatus");
              } else {
                // fallback
                this.toastService.showErrorByKey("Toasts.ErrorDuringUserEditRequestStatus");
              }
            }
          }
          ,);
      });
  }

  public openStatusEditSendgroupDetailsModal(editSendgroup: StatusUpdateSendGroupModel) {
    if (editSendgroup === undefined || !editSendgroup) {
      return;
    }
    this.modalOpenerService.StatusEditSendgroupDetails(editSendgroup);
  }
  public async resendRequestInviteForActor(actor: ActorStatusInformationModel) {
    // TODO: BETER ERROR, THIS TALKS ABOUT REQUEST, WHILE WE ARE INVITING AN ACTOR
    (await this.actorApiClient.ResendInvite(this.requestId, actor.inviteId)).subscribe({
      error: () => this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestsResendError + ""),
      next: () => {
        let reminderSent = this.translateService.instant('Toasts.ReminderSent', { name: actor.fullName });
        this.toastService.showSuccess(this.translations.toastsSent, reminderSent);
      }
    },
    );
  }

  public async resendRequestInviteForSendGroup(sendGroup: ActorSendGroupStatusInformationModel) {
    // TODO: BETER ERROR, THIS TALKS ABOUT REQUEST, WHILE WE ARE INVITING AN ACTOR
    (await this.sendgroupService.resendSendGroupInvites(this.requestId, sendGroup.sendGroupGuid)).subscribe({
      error: () => this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestsResendError + ""),
      next: () => {
        let reminderSent = this.translateService.instant('Toasts.ReminderSent', { name: sendGroup.sendGroupName });
        this.toastService.showSuccess(this.translations.toastsSent, reminderSent);
      }
    },
    );
  }

  public async openPreview(document: any) {
    if (document && document.documentType == DocumentTypeEnum.RegularPdf) {
      this.getDocumentForOpeningPreview(document);
    } else if (document && (XbrlDocumentTypes.find(x => document.documentType == x) || DocumentTypesForViewingAsHtml.includes(document.documentType))) {
      this.modalOpenerService.ViewXblrOrTxt(this.request, document);
    } else if (document.documentType == DocumentTypeEnum.RegularXml || document.documentType == DocumentTypeEnum.DetachedSignature) {
      this.modalOpenerService.ViewXml(this.request.id!, document);
    }
  }

  public async OpenDocumentDetais(document: StatusDocumentModel) {
    this.modalOpenerService.DocumentDetails(document);
  }

  public getRightActor(actorId: string) {
    // TODO



    if (this.request.actors.findIndex(actor => actor.id === actorId) > -1) {
      // this.actor = this.request.actors[this.request.actors.findIndex(actor => actor.id === actorId)];
    } else {
      this.actor = new ActorStatusInformationModel;
    }
  }

  public setSelectedActorId(actorId: string) {
    if (this.actorId == actorId) {
      // NO CHANGE
      return;
    }
    this.actorId$.next(actorId);
    this.actorId = actorId;
    this.getSendgroupsForRequestList();
  }


  // #region Private methods
  private getTranslations() {
    this.translateService.get([
      'Toasts.RequestsDownloadError',
      'Toasts.RequestsPurgeError',
      'Toasts.RequestsDeleteError',
      'Toasts.RequestsExportError',
      'Toasts.DocumentDeleteError',
      'Toasts.DocumentExportError',
      'Toasts.RequestOwnerChanged',
      'Toasts.Error',
      'Toasts.SingleNotAllowedWithdrawError',
      'Toasts.SingleNotAllowedDeleteError',
      'Toasts.RequestPurged',
      'Toasts.RequestRestored',
      'Toasts.Purged',
      'Toasts.Changed',
      'Toasts.Restored',
      'Toasts.DownloadTitle',
      'Toasts.DownloadBody',
      'Toasts.ExportTitle',
      'Toasts.ExportBody',
      'Request.Status.AllDocuments',
      'Request.Status.Day',
      'Request.Status.Days',
      'Request.Status.Month',
      'Request.Status.Months',
      'Toasts.RequestsResendOne',
      'Toasts.RequestsResendError',
      'Toasts.Sent',
      'Toasts.Error',
      "Toasts.PersonChangedNoInvite",
      "Toasts.PersonChangedWithInvite",
      "Toasts.DeadlineChangedWithInvite",
      "Toasts.DeadlineChangedNoInvite",
      "Toasts.Success",
      "Toasts.ClipboardError",
      "Toasts.ClipboardNotSupported",
      "Toasts.AllowedSignatureProvidersEdited",
      "Toasts.RequestSuccessfullyUpdated"
    ]).subscribe(translation => {
      this.translations.toastsRequestsDownloadError = translation['Toasts.RequestsDownloadError'];
      this.translations.toastsRequestsPurgeError = translation['Toasts.RequestsPurgeError'];
      this.translations.toastsRequestsDeleteError = translation['Toasts.RequestsDeleteError'];
      this.translations.toastsRequestsExportError = translation['Toasts.RequestsExportError'];
      this.translations.toastsDocumentDeleteError = translation['Toasts.DocumentDeleteError'];
      this.translations.toastsDocumentExportError = translation['Toasts.DocumentExportError'];
      this.translations.toastsRequestOwnerChanged = translation['Toasts.RequestOwnerChanged'];
      this.translations.error = translation['Toasts.Error'];
      this.translations.notAllowedWithdrawError = translation['Toasts.SingleNotAllowedWithdrawError'];
      this.translations.notAllowedDeleteError = translation['Toasts.SingleNotAllowedDeleteError'];
      this.translations.toastsRequestPurged = translation['Toasts.RequestPurged'];
      this.translations.toastsRequestRestored = translation['Toasts.RequestRestored'];
      this.translations.toastsPurged = translation['Toasts.Purged'];
      this.translations.toastsChanged = translation['Toasts.Changed'];
      this.translations.toastsRestored = translation['Toasts.Restored'];
      this.translations.DownloadTitle = translation['Toasts.DownloadTitle'];
      this.translations.DownloadBody = translation['Toasts.DownloadBody'];
      this.translations.ExportTitle = translation['Toasts.ExportTitle'];
      this.translations.ExportBody = translation['Toasts.ExportBody'];
      this.translations.AllDocuments = translation['Request.Status.AllDocuments'];
      this.translations.Day = translation['Request.Status.Day'];
      this.translations.Days = translation['Request.Status.Days'];
      this.translations.Month = translation['Request.Status.Month'];
      this.translations.Months = translation['Request.Status.Months'];
      this.translations.toastsRequestsResendOne = translation['Toasts.RequestsResendOne'];
      this.translations.toastsRequestsResendError = translation['Toasts.RequestsResendError'];
      this.translations.toastsSent = translation['Toasts.Sent'];
      this.translations.error = translation['Toasts.Error'];
      this.translations.success = translation['Toasts.Success'];
      this.translations.requestSuccessfullyUpdated = translation['Toasts.RequestSuccessfullyUpdated'];
      this.translations.toastAllowedSignatureProvidersEdited = translation['Toasts.AllowedSignatureProvidersEdited'];
      this.translations.toastPersonChangedNoInvite = translation['Toasts.PersonChangedNoInvite'];
      this.translations.toastPPersonChangedWithInvite = translation['Toasts.PersonChangedWithInvite'];
      this.translations.toastDeadlineChangedWithInvite = translation['Toasts.DeadlineChangedWithInvite'];
      this.translations.toastDeadlineChangedNoInvite = translation['Toasts.DeadlineChangedNoInvite'];
      this.translations.toastsClipboardError = translation['Toasts.ClipboardError'];
      this.translations.toastsClipboardNotSupported = translation['Toasts.ClipboardNotSupported'];
    });
  }

  private getRequest() {
    this.statusRequestApiClient.Get(this.requestId).then(dossierResp => {
      lastValueFrom(dossierResp)
        .then((request: StatusRequestModel) => {
          this.request = request;
          this.getSendgroupsForRequestList();
          this.getEventLog();
          this.getNotes();
          if (this.request.workflowId) {
            this.initializeForWorkflow(this.request.workflowId);
            this.getSignatureProviders();
          }
          this.hasEditRights = this.hasUserEditRights();
          this.allowedToChangeOwnership = this.isUserAllowedToChangeOwnership();
          this.selectedRequest$.next(this.request);
          this.requestService.extendDeadlinesIfNeeded(this.requestId).subscribe();
        }).catch((error) => {
          this.loggingService.logException(error);
          this.openDashboard();
        });
    });
  }

  private getNotes() {
    this.noteApiClient.Get(this.requestId).subscribe({
      next: (notes: NoteModel[]) => {
        this.notes = notes;
        this.notes$.next(this.notes);
      },
      error: (error: HttpErrorResponse) => this.loggingService.logException(error)
    });
  }
  private getEventLog() {
    this.eventLogApiClient.Get(this.requestId).subscribe({
      next: (events: any) => {
        this.eventlog = events;
        this.eventLog$.next(this.eventlog);
      },
      error: (error: HttpErrorResponse) => this.loggingService.logException(error)
    });
  }

  private getSendgroupsForRequestList() {
    this.actorApiClient.GetForStatusOverview(this.requestId).subscribe((sendgroups) => {
      this.setSendgroups(sendgroups);
    });
  }

  private getDocumentForOpeningPreview(doc: DocumentModel) {
    this.documentService.getDocument(this.request.id!, doc.id)
      .then(data => {
        lastValueFrom(data)
          .then((value: any) => {
            doc.id = value.id;
            doc.name = value.name;
            doc.fileSize = value.documentSize;
            doc.documentStatus = value.status;
            doc.pages = value.pages;
            this.request.documentInfo.filter((s: { id: string; }) => s.id === doc.id)[0].fileSize = doc.fileSize;
            this.request.documentInfo.filter((s: { id: string; }) => s.id === doc.id)[0].documentStatus = value.status;
            this.request.documentInfo.filter((s: { id: string; }) => s.id === doc.id)[0].pages = doc.pages;
            this.openPdfPreview(doc).catch((error) => this.loggingService.logException(error));
          })
          .catch((error) => this.loggingService.logException(error));
      })
      .catch((error) => this.loggingService.logException(error));
  }

  private saveFile(blobContent: Blob, fileName: string) {
    const blob = new Blob([blobContent], { type: 'application/octet-stream' });
    saveAs(blob, fileName);
  }

  private async openPdfPreview(document: DocumentModel) {
    if (this.modalService.hasOpenModals()) return;
    let documentId: string = document.id;
    let doc = this.request.documentInfo.find(d => d.id === documentId);
    let ngbModalOptions: NgbModalOptions;
    if (!this.isMobile) {
      ngbModalOptions = {
        backdrop: 'static',
        keyboard: false,
        centered: false,
        modalDialogClass: 'pkiMd',
        windowClass: 'addSignatureFieldModal'
      };
    }
    else {
      ngbModalOptions = {
        backdrop: 'static',
        keyboard: false,
        centered: false,
        size: 'xl',
        windowClass: 'addSignatureFieldModalMobile'
      };
    }
    let modalRef: any = this.modalService.open(PdfPreparationDialogForStatus, ngbModalOptions);
    let modal: PdfPreparationDialogForStatus = modalRef.componentInstance;

    //TODO: refactor to proper inputs and events
    modal.document = doc!;
    modal.requestGuid = this.request.id;
    //modal.isMobile = this.isMobile!;
    modal.place = false;
    modal.displayfields = true;
    modal.setMoreSignaturefields = false;
    modal.showEditDocument = false;
    modal.actors = doc!.actors;
    modal.document = doc!;
    modal.getRequest.subscribe(() => this.request);
    if (!this.isMobile) {
      modal.zoomLevelChanged.subscribe(async (item: any) => {
        modalRef._windowCmptRef.instance.modalDialogClass = item.className;
        setTimeout(() => modal.afterZoomLevelChanged(), 100);
      });
    }
  }

  async copyToClipboard(Guid: string) {
    try {
      if (!navigator.clipboard) {
        this.toastService.showError(this.translations.error, this.translations.toastsClipboardNotSupported);
      }
      await this.clipboardService.Copy(Guid);
      this.toastService.showCopied();
    }
    catch (error: any) {
      this.toastService.showError(this.translations.error, this.translations.toastsClipboardError);
    }
  }

  public updateSendgroup(sendGroupUpdateModel: StatusUpdateSendGroupModel) {
    this.sendgroupService.updateSendGroup(this.requestId, sendGroupUpdateModel).subscribe({
      next: () => {
        this.toastService.showSaved();
      },
      error: () => { }
    });
  }

  async openEmailPreview(event: any) {
    let actor = this.request.actors.filter(item => item.id === event.inviteId && item.action)[0];
    if (actor && actor !== undefined) {
      (await this.actorApiClient.getEmailTemplate(this.requestId, actor.id)).subscribe({
        next: (value: any) => {
          const modalRef = this.modalService.open(EmailPreviewDialogComponent, EmailPreviewModalOptions);
          modalRef.componentInstance.emailTemplate = value;
        }
      });
    }
  }

  async openChangeSignatureProvider(event: any) {
    let actor = this.request.actors.filter(item => item.id === event.inviteId && item.action)[0];
    if (actor && actor !== undefined) {
      const modalRef = this.modalService.open(EditSignatureDataDialogComponent, EditSignatureDataModalOptions);
      modalRef.componentInstance.actorId = actor.id;
      modalRef.closed.subscribe(async (result: ActorStatusInformationModel | null) => {
        if (result === null) {
          return;
        }
        actor.allowedQtsps = result.allowedQtsps;
        (await this.actorApiClient.updateActionToPerson(this.requestId!, actor.documentId, actor)).subscribe({
          next: () => {
            this.toastService.showSuccess(this.translations.success, this.translations.toastAllowedSignatureProvidersEdited);
          }, error: (error: HttpErrorResponse) => {
            this.loggingService.logException(error);
          }
        });
      });
    }
  }

  public async getRequestsForPerson(personGuid: string): Promise<UpdateExistingRequestActorModel[]> {
    // let call = await this.personApiClient.getRequestsForPerson(this.requestId, personGuid);
    let getRequests = await this.personApiClient.getRequestsForPerson(this.requestId, personGuid);
    let requests = await lastValueFrom(getRequests);
    return requests;
  }

  public convertEventToTranslatedContent(event: EventLogModel): any {
    let paramZero: any = event.descriptionParametersZero;
    let paramZeroData: string | null = paramZero;

    let paramOne: any = event.descriptionParametersOne;
    let paramOneData: string | null = paramOne;

    let paramTwo: any = event.descriptionParametersTwo;
    let paramTwoData: string | null = paramTwo;

    // Specific DeadlineDaysEdited event format
    if (event.eventType == EventLogTypeEnum.DeadlineDaysEdited) {
      if (paramZero === 1) {
        paramZeroData = this.translateService.instant('Request.Status.DaysToReadableText.Selected_Day', { days: paramZero });
      }
      else if (paramZero > 1 && paramZero < 31) {
        paramZeroData = this.translateService.instant('Request.Status.DaysToReadableText.Selected_Days', { days: paramZero });
      }
      else if (paramZero === 31) {
        paramZeroData = this.translateService.instant('Request.Status.DaysToReadableText.Selected_Month', { month: 1 });
      }
      else if (paramZero > 31) {
        paramZeroData = this.translateService.instant('Request.Status.DaysToReadableText.Selected_Months', { months: paramZero === 62 ? 2 : 3 });
      }
    }
    if (event.eventType == EventLogTypeEnum.DeadlineEdited) {
      // a parameter is needed:
      if (!this.stringHelper.NullOrEmpty(event.descriptionParametersZero)) {
        // new expected format: 2025-09-24T08:40:00.0000000Z
        if (this.stringHelper.startsWithYearDash(event.descriptionParametersZero)) {
          // now its a proper Date format and we can try and parse it:
          let formattedDate = new Date(event.descriptionParametersZero);
          let formatDatea = formatDate(formattedDate, 'dd-MM-yy HH:mm', 'en-US');
          event.descriptionParametersZero = formatDatea;
        }
      }
    }
    if (event.eventType == EventLogTypeEnum.WorkgroupChanged) {
      paramZeroData = this.authenticationService.getWorkGroupName(paramZero);
      paramOneData = this.authenticationService.getWorkGroupName(paramOne);
    }
    return this.translateService.instant('EventLog.' + event.eventType, { sourceName: event.sourceDisplayName, paramZero: paramZeroData, paramOne: paramOneData, paramTwo: paramTwoData });
  }

  private async getSignatureProviders() {
    this.engineService.getSignatureProviders().then(providers => {
      this._signatureProviders = providers;
      this.signatureProviders$.next(this._signatureProviders);
    }).catch(error => {
      this.loggingService.logException(error);
    });
  }

  public async updateRequest(request: StatusRequestModel) {
    (await this.dossierService.updateDossier(request.id, request.requestName, request.workgroupGuid, request.clearancelevel)).subscribe(() => {
      let model: BuilderRequestAutoExportSettingsModel = {
        requestGuid: request.id!,
        enabled: request.autoExportModel.cloudItem != null ? request.autoExportModel.enabled : false,
        cloudItem: request.autoExportModel.cloudItem || null
      }
      this.statusRequestApiClient.SetAutoExport(model).then(autoExportResp => {
        this.toastService.showSuccess(this.translations.success, this.translations.requestSuccessfullyUpdated);
      }).catch(error => {
        this.loggingService.logException(error);
      });
    }, error => {
      this.loggingService.logException(error);
    });
  }

  public getProviderValue(provider: Provider) {
    if (provider.parameters?.length > 0) {
      return provider.parameters[0];
    }
    else if (provider.signatureProviderType === SignatureProviderTypeEnum.TokenCertificateSignatureProvider) {
      return "Token";
    }
    return provider.signatureProviderType;
  }

  private async initializeForWorkflow(workflowId: string) {
    this.workflowData = await this.workflowService.getWorkflowById(workflowId);
    this.checkWorkflows();
  }

  private async checkWorkflows() {
    this.placeholdersStep3 = (this.workflowData?.step3?.placeholders && this.workflowData?.step3?.placeholders.length > 0) ?? false;
    this.allowedSignatureProvidersByWorkflow = this.workflowData?.step3.allowedQtsps ?? [];

    if (this.placeholdersStep3) {
      this.workflowData!.step3.placeholders.forEach(placeholder => {
        if (this.allowedSignatureProvidersByPlaceholder.length === 0 || this.allowedSignatureProvidersByPlaceholder.some(s => s.placeholderId == placeholder.placeholderId)) {
          this.allowedSignatureProvidersByPlaceholder.push({ placeholderId: placeholder.placeholderId, allowedQTSP: placeholder.allowedQtsps });
        }
      });
    }
  }
}
